import React, { useMemo } from 'react';
import { FunnelIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import AutoSuggest from '../FormComponent/AutoSuggest';
import Selectbox from '../FormComponent/Selectbox';
import DatePicker from '../FormComponent/DatePicker';
import ButtonComponent from '../customButton';
import pageStyles from '../../styles/feedback/FeedbackPage.module.scss';
import { FEEDBACK_TYPE_OPTIONS, FEEDBACK_STATUS_OPTIONS } from '../../utils/feedbackUtils';

const FILTER_CONTENT = {
  labels: {
    type: 'Type',
    status: 'Status',
    organisation: 'Organisation',
    startDate: 'Start Date',
    endDate: 'End Date',
    clearAll: 'Clear All',
    filters: 'Filters',
    organisationPlaceholder: 'Select organisation',
    typePlaceholder: 'Select type',
  },
};

const extractValues = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'object' && item !== null) {
        return item.value || item.id || item;
      }
      return item;
    });
  }
  if (typeof value === 'object' && value !== null) {
    return [value.value || value.id || value];
  }
  return [value];
};

const getOptionObjects = (filterValues, optionList) => {
  if (!Array.isArray(filterValues) || filterValues.length === 0) return [];
  return filterValues
    .map((val) => optionList.find((opt) => opt.value === val || opt.id === val))
    .filter(Boolean);
};

const FeedbackFilter = ({
  filters,
  onFilterChange,
  organizations,
  isCollapsed,
  onToggleCollapse,
  activeFilterChips,
}) => {
  const activeFilterCount = activeFilterChips.length;

  const groupedChips = useMemo(() => {
    const groups = {};
    activeFilterChips.forEach((chip) => {
      if (!groups[chip.label]) groups[chip.label] = [];
      groups[chip.label].push(chip.value);
    });
    return Object.entries(groups).map(([label, values]) => ({ label, values }));
  }, [activeFilterChips]);

  const orgOptions = useMemo(
    () => organizations.map((org) => ({
      value: String(org.id ?? org.value),
      label: org.name ?? org.label,
    })),
    [organizations],
  );

  const handleOrganizationChange = (e) => {
    const value = e?.target?.value;
    const normalized = (typeof value === 'object' && value !== null && !Array.isArray(value))
      ? (value.value || value.id || '')
      : (value ?? '');
    onFilterChange({
      ...filters,
      org_id: normalized === null ? '' : String(normalized),
    });
  };

  const handleTypeChange = (e) => {
    onFilterChange({
      ...filters,
      type: extractValues(e?.target?.value),
    });
  };

  const handleChange = (name, value) => {
    onFilterChange({ ...filters, [name]: value });
  };

  const handleClear = () => {
    onFilterChange({ type: [], status: '', org_id: '', start_date: '', end_date: '' });
  };

  const hasActiveFilters = activeFilterCount > 0;

  const selectedOrg = orgOptions.find(
    (opt) => opt.value === String(filters.org_id) || opt.id === filters.org_id,
  ) || null;

  return (
    <div className={`${pageStyles.sidePanel} ${isCollapsed ? pageStyles.collapsed : pageStyles.expanded}`}>
      {/* Header */}
      <div className={`${pageStyles.sidePanelHeader} ${isCollapsed ? pageStyles.collapsed : ''}`}>
        {!isCollapsed && (
          <div className={pageStyles.sidePanelHeaderLeft}>
            <FunnelIcon style={{ width: 20, height: 20, color: '#2563EB' }} />
            <h3 className={pageStyles.sidePanelTitle}>{FILTER_CONTENT.labels.filters}</h3>
          </div>
        )}
        {isCollapsed && (
          <div title={FILTER_CONTENT.labels.filters} className={pageStyles.sidePanelIconContainer}>
            <FunnelIcon style={{ width: 24, height: 24, color: '#2563EB' }} />
          </div>
        )}
        {!isCollapsed && (
          <div className={pageStyles.sidePanelHeaderRight}>
            <button
              title="Collapse"
              onClick={onToggleCollapse}
              className={pageStyles.collapseButton}
            >
              <ChevronRightIcon style={{ width: 16, height: 16 }} />
            </button>
          </div>
        )}
      </div>

      {/* Collapsed view */}
      {isCollapsed && (
        <div className={pageStyles.collapsedView}>
          <button
            title="Expand Filters"
            onClick={onToggleCollapse}
            className={pageStyles.expandButton}
          >
            <ChevronLeftIcon style={{ width: 20, height: 20 }} />
          </button>
          {activeFilterCount > 0 && (
            <div
              className={pageStyles.collapsedFilterBadge}
              onMouseEnter={(e) => {
                const tooltip = e.currentTarget.querySelector(`.${pageStyles.collapsedFilterTooltip}`);
                if (tooltip) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  tooltip.style.top = `${rect.top + rect.height / 2}px`;
                  tooltip.style.right = `${window.innerWidth - rect.left + 12}px`;
                }
              }}
            >
              <span className={pageStyles.collapsedFilterCount}>{activeFilterCount}</span>
              <div className={pageStyles.collapsedFilterTooltip}>
                <div className={pageStyles.collapsedFilterTooltipArrow} />
                <div className={pageStyles.collapsedFilterTooltipHeader}>
                  Active Filters ({activeFilterCount})
                </div>
                <div className={pageStyles.collapsedFilterTooltipBody}>
                  {groupedChips.length > 0 ? (
                    groupedChips.map((group, index) => (
                      <div key={index} className={pageStyles.collapsedFilterTooltipItem}>
                        <span className={pageStyles.collapsedFilterTooltipLabel}>{group.label}</span>
                        <div className={pageStyles.collapsedFilterTooltipValues}>
                          {group.values.map((val, idx) => (
                            <span key={idx} className={pageStyles.collapsedFilterTooltipValue}>{val}</span>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <span className={pageStyles.collapsedFilterTooltipEmpty}>No active filters</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expanded view */}
      {!isCollapsed && (
        <div className={pageStyles.expandedView}>
          <AutoSuggest
            label={FILTER_CONTENT.labels.organisation}
            name="org_id"
            value={selectedOrg}
            options={orgOptions}
            placeholder={FILTER_CONTENT.labels.organisationPlaceholder}
            allowClear
            multiple={false}
            onChange={handleOrganizationChange}
          />
          <AutoSuggest
            label={FILTER_CONTENT.labels.type}
            name="type"
            value={getOptionObjects(filters.type || [], FEEDBACK_TYPE_OPTIONS)}
            options={FEEDBACK_TYPE_OPTIONS}
            placeholder={FILTER_CONTENT.labels.typePlaceholder}
            allowClear
            multiple
            onChange={handleTypeChange}
          />
          <Selectbox
            label={FILTER_CONTENT.labels.status}
            name="status"
            value={filters.status || ''}
            options={FEEDBACK_STATUS_OPTIONS}
            onChange={(e) => handleChange('status', e?.target?.value)}
          />
          <DatePicker
            label={FILTER_CONTENT.labels.startDate}
            name="start_date"
            value={filters.start_date || ''}
            onChange={(e) => handleChange('start_date', e.target.value)}
          />
          <DatePicker
            label={FILTER_CONTENT.labels.endDate}
            name="end_date"
            value={filters.end_date || ''}
            onChange={(e) => handleChange('end_date', e.target.value)}
          />
          {hasActiveFilters && (
            <ButtonComponent
              variant="outline"
              size="small"
              ctaText={FILTER_CONTENT.labels.clearAll}
              startIcon={<XMarkIcon className="h-4 w-4" />}
              onClick={handleClear}
              className={pageStyles.clearButton}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackFilter;
